import { SHIPMENT_STATUS_LABELS, SHIPMENT_STATUS_VALUES } from '@/lib/shipment'
import { EMPTY_DATE_RANGE, PERIOD_CHIP_KEY, getPeriodChipLabel, hasDateRange, parseDateRange, writeDateRange } from '@/lib/filters'
import type { DateRange } from '@/lib/filters'
import type { ShipmentStatus } from '@/api/types'

export interface ShipmentFacetFilters {
  statuses: ShipmentStatus[]
  period: DateRange
}

// Ett chip vet själv hur filtren ser ut utan det, så sidan slipper tolka nycklar.
export interface ShipmentFacetChip {
  key: string
  label: string
  withoutChip: ShipmentFacetFilters
}

export const STATUS_PARAM = 'status'
export const STATUS_GROUP_LABEL = 'Status'

// Filtrerar genom den kanoniska listan: okända värden försvinner, dubbletter slås ihop och ordningen blir alltid densamma.
function parseStatuses(values: string[]): ShipmentStatus[] {
  return SHIPMENT_STATUS_VALUES.filter((status) => values.includes(status))
}

export function parseShipmentFacetFilters(params: URLSearchParams): ShipmentFacetFilters {
  return { statuses: parseStatuses(params.getAll(STATUS_PARAM)), period: parseDateRange(params) }
}

// Skriver om facetterna från grunden så att avmarkerade val inte ligger kvar i URL:en.
export function writeShipmentFacetFilters(params: URLSearchParams, filters: ShipmentFacetFilters): void {
  params.delete(STATUS_PARAM)

  for (const status of filters.statuses) {
    params.append(STATUS_PARAM, status)
  }

  writeDateRange(params, filters.period)
}

// Räknar bara det som ligger i Filter-menyn – perioden har en egen knapp.
export function countShipmentMenuFacets(filters: ShipmentFacetFilters): number {
  return filters.statuses.length
}

export function toggleShipmentStatus(filters: ShipmentFacetFilters, status: ShipmentStatus): ShipmentFacetFilters {
  const isSelected = filters.statuses.includes(status)
  const next = isSelected ? filters.statuses.filter((candidate) => candidate !== status) : [...filters.statuses, status]

  return { ...filters, statuses: SHIPMENT_STATUS_VALUES.filter((candidate) => next.includes(candidate)) }
}

export function listShipmentFacetChips(filters: ShipmentFacetFilters): ShipmentFacetChip[] {
  const chips: ShipmentFacetChip[] = filters.statuses.map((status) => ({
    key: `${STATUS_PARAM}:${status}`,
    label: `${STATUS_GROUP_LABEL}: ${SHIPMENT_STATUS_LABELS[status]}`,
    withoutChip: { ...filters, statuses: filters.statuses.filter((candidate) => candidate !== status) }
  }))

  if (hasDateRange(filters.period)) {
    chips.push({ key: PERIOD_CHIP_KEY, label: getPeriodChipLabel(filters.period), withoutChip: { ...filters, period: EMPTY_DATE_RANGE } })
  }

  return chips
}
